import clsx from 'clsx'
import { ChevronDown, ChevronUp, Download, Trophy } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { AdminDashboardResponse, AdminVendorRow } from '@/types'
import { ErrorNote, LoadingScreen } from '@/components/ui'
import { api } from '@/lib/api'
import { currency } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { AdminSection } from './AdminSection'

type SortKey = 'fullName' | 'branch' | 'sales' | 'orders' | 'clicks'
const NUM_KEYS: SortKey[] = ['sales', 'orders', 'clicks']

const COLUMNS: { key: SortKey; label: string; num?: boolean }[] = [
  { key: 'fullName', label: 'Vendedor' },
  { key: 'branch', label: 'Sucursal' },
  { key: 'sales', label: 'Ventas', num: true },
  { key: 'orders', label: 'Pedidos', num: true },
  { key: 'clicks', label: 'Clics', num: true },
]

function csvCell(v: string | number): string {
  return `"${String(v).replace(/"/g, '""')}"`
}

function exportCsv(rows: AdminVendorRow[]) {
  const header = ['Posicion', 'Nombre', 'Nro', 'Sucursal', 'Ventas', 'Pedidos', 'Clics']
  const lines = rows.map((r) =>
    [r.rankPosition, r.fullName, r.vendorNumber, r.branch, Math.round(r.sales), r.orders, r.clicks]
      .map(csvCell)
      .join(',')
  )
  const csv = [header.map(csvCell).join(','), ...lines].join('\n')
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'vendedores-mes.csv'
  a.click()
  URL.revokeObjectURL(a.href)
}

export function AdminDashboard() {
  const { data, loading, error, reload } = useAsync(
    () => api.get<AdminDashboardResponse>('/admin/dashboard'),
    []
  )
  const [branch, setBranch] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'sales', dir: 'desc' })

  const branches = useMemo(
    () => Array.from(new Set((data?.rows ?? []).map((r) => r.branch).filter(Boolean))).sort(),
    [data]
  )

  const rows = useMemo(() => {
    let list = data?.rows ?? []
    if (branch) list = list.filter((r) => r.branch === branch)
    const factor = sort.dir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      const av = a[sort.key]
      const bv = b[sort.key]
      if (NUM_KEYS.includes(sort.key)) return ((av as number) - (bv as number)) * factor
      return String(av).localeCompare(String(bv)) * factor
    })
  }, [data, branch, sort])

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: NUM_KEYS.includes(key) ? 'desc' : 'asc' }))
  }

  if (loading) return <LoadingScreen label="Cargando el equipo…" />
  if (error || !data) return <ErrorNote message={error?.message ?? 'No se pudo cargar.'} onRetry={reload} />

  const { summary, topPerformerId } = data
  const conversionPct = `${(summary.avgConversion * 100).toFixed(1)}%`

  return (
    <AdminSection title="Dashboard del equipo">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="stat-card"><span className="stat-value text-lg">{currency(summary.totalSales)}</span><span className="stat-label">Ventas del equipo</span></div>
          <div className="stat-card"><span className="stat-value text-lg">{summary.totalOrders}</span><span className="stat-label">Pedidos</span></div>
          <div className="stat-card"><span className="stat-value text-lg">{summary.totalClicks}</span><span className="stat-label">Clics</span></div>
          <div className="stat-card"><span className="stat-value text-lg">{conversionPct}</span><span className="stat-label">Conversión prom.</span></div>
        </div>

        <div className="flex items-center gap-2">
          <select
            className="field-input flex-1 py-2"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
          >
            <option value="">Todas las sucursales</option>
            {branches.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <button className="btn-secondary shrink-0" onClick={() => exportCsv(rows)} disabled={rows.length === 0}>
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>

        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-muted">Sin vendedores para mostrar.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-line text-ink-muted">
                  <th className="py-2 pr-2 font-medium">#</th>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={clsx('py-2 pr-2 font-medium', c.num && 'text-right')}>
                      <button className="inline-flex items-center gap-0.5" onClick={() => toggleSort(c.key)}>
                        {c.label}
                        {sort.key === c.key && (sort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.vendorId}
                    className={clsx('border-b border-line/60', r.vendorId === topPerformerId && 'accent-bg-soft')}
                  >
                    <td className="py-2 pr-2 text-ink-faint">
                      {r.vendorId === topPerformerId ? <Trophy className="h-3.5 w-3.5 text-gold" /> : r.rankPosition}
                    </td>
                    <td className="py-2 pr-2">
                      <div className="font-semibold text-ink">{r.fullName}</div>
                      <div className="text-[10px] text-ink-faint">Nº {r.vendorNumber}</div>
                    </td>
                    <td className="py-2 pr-2 text-ink-muted">{r.branch}</td>
                    <td className="py-2 pr-2 text-right font-semibold text-ink">{currency(r.sales)}</td>
                    <td className="py-2 pr-2 text-right text-ink">{r.orders}</td>
                    <td className="py-2 pr-2 text-right text-ink">{r.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminSection>
  )
}
